// ============================================================================
// Session query + mutation hooks (incl. the trainer-wide calendar feed).
// ----------------------------------------------------------------------------
// The calendar query drives the dashboard. Set/cardio logging and
// apply-template all invalidate the session detail; create/update/delete also
// touch the calendar + client roster. The live-sync layer additionally
// invalidates sessions root on any `session`/`set`/`cardio` event from other
// devices.
// ============================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { qk, sessionsApi } from "../api";
import type {
  CardioInput,
  CheckinInput,
  Session,
  SessionInput,
  SetInput,
} from "../api";
import type { CalendarRange } from "../api/sessions";

export function useCalendar(range: CalendarRange = {}) {
  return useQuery({
    queryKey: qk.sessions.calendar(range.start, range.end),
    queryFn: ({ signal }) => sessionsApi.calendar(range, signal),
  });
}

export function useClientSessions(clientId: number | undefined) {
  return useQuery({
    queryKey: qk.clients.sessions(clientId ?? -1),
    queryFn: ({ signal }) =>
      sessionsApi.listForClient(clientId as number, signal),
    enabled: clientId !== undefined && clientId >= 0,
  });
}

export function useSession(sessionId: number | undefined) {
  return useQuery({
    queryKey: qk.sessions.detail(sessionId ?? -1),
    queryFn: ({ signal }) => sessionsApi.get(sessionId as number, signal),
    enabled: sessionId !== undefined && sessionId >= 0,
  });
}

function invalidateSessionWorld(
  qc: ReturnType<typeof useQueryClient>,
  clientId?: number,
) {
  void qc.invalidateQueries({ queryKey: qk.sessions.root });
  void qc.invalidateQueries({ queryKey: qk.clients.root });
  if (clientId !== undefined) {
    void qc.invalidateQueries({ queryKey: qk.clients.sessions(clientId) });
  }
}

export function useCreateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, body }: { clientId: number; body: SessionInput }) =>
      sessionsApi.create(clientId, body),
    onSuccess: (_session, { clientId }) => invalidateSessionWorld(qc, clientId),
  });
}

export function useUpdateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      body,
    }: {
      sessionId: number;
      body: SessionInput;
    }) => sessionsApi.update(sessionId, body),
    onMutate: async ({ sessionId, body }) => {
      const detailKey = qk.sessions.detail(sessionId);
      await qc.cancelQueries({ queryKey: detailKey });
      const prev = qc.getQueryData<{ session: Session }>(detailKey);
      if (prev?.session) {
        qc.setQueryData(detailKey, {
          ...prev,
          session: { ...prev.session, ...body },
        });
      }
      return { prev, sessionId };
    },
    onError: (_err, { sessionId }, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.sessions.detail(sessionId), ctx.prev);
    },
    onSettled: (_data, _err, { sessionId }) => {
      void qc.invalidateQueries({ queryKey: qk.sessions.detail(sessionId) });
      invalidateSessionWorld(qc);
    },
  });
}

export function useDeleteSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: number) => sessionsApi.remove(sessionId),
    onSuccess: () => invalidateSessionWorld(qc),
  });
}

/**
 * Mark a session ATTENDED (records the trainer-side check-in). The server sets
 * attendance 'attended' + checked_in_at and flips status to 'completed', so the
 * session counts as delivered. Returns the refreshed session row; we seed the
 * detail cache with it and invalidate the calendar + reports (no-show/attended
 * affects the income figures).
 */
export function useCheckinSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      body,
    }: {
      sessionId: number;
      body?: CheckinInput;
    }) => sessionsApi.checkin(sessionId, body ?? {}),
    onSuccess: (session) => {
      seedSessionDetail(qc, session);
      invalidateSessionWorld(qc, session.client_id);
    },
  });
}

/** Mark a session a NO-SHOW (kept on the row but excluded from income). */
export function useNoShowSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: number) => sessionsApi.noShow(sessionId),
    onSuccess: (session) => {
      seedSessionDetail(qc, session);
      invalidateSessionWorld(qc, session.client_id);
    },
  });
}

/** Mint/rotate a per-session QR check-in token (no cache writes; UI holds it). */
export function useCheckinQr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: number) => sessionsApi.checkinQr(sessionId),
    // Rotating the token doesn't change displayed data, but the broadcast does
    // refresh other devices; keep the local calendar in step too.
    onSuccess: () => invalidateSessionWorld(qc),
  });
}

/** Merge a fresh session row into its detail-cache entry (if one exists). */
function seedSessionDetail(
  qc: ReturnType<typeof useQueryClient>,
  session: Session,
) {
  const key = qk.sessions.detail(session.id);
  const prev = qc.getQueryData<{ session: Session }>(key);
  if (prev?.session) {
    qc.setQueryData(key, { ...prev, session: { ...prev.session, ...session } });
  }
}

export function useAddSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, body }: { sessionId: number; body: SetInput }) =>
      sessionsApi.addSet(sessionId, body),
    onSuccess: (_set, { sessionId }) => {
      void qc.invalidateQueries({ queryKey: qk.sessions.detail(sessionId) });
    },
  });
}

export function useAddCardio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      body,
    }: {
      sessionId: number;
      body: CardioInput;
    }) => sessionsApi.addCardio(sessionId, body),
    onSuccess: (_cardio, { sessionId }) => {
      void qc.invalidateQueries({ queryKey: qk.sessions.detail(sessionId) });
    },
  });
}

export function useApplyTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      templateId,
    }: {
      sessionId: number;
      templateId: number;
    }) => sessionsApi.applyTemplate(sessionId, templateId),
    onSuccess: (_res, { sessionId }) => {
      void qc.invalidateQueries({ queryKey: qk.sessions.detail(sessionId) });
    },
  });
}
